import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import {
    TelemetryConfiguration,
    FeatureUsageEvent,
    ProActiveAlertEvent
} from './TelemetryInterface';
import { TelemetryDimensions, TelemetryEventName } from './TelemetryConstants';
import { sanitizeDependencyEnvelope } from './TelemetryUrlSanitizer';

/**
 * Application Insights wrapper for the Purview UI.
 *
 * Design principles:
 * - **No PII.** Only pseudonymous identifiers (Entra object id, tenant id) and
 *   application-defined enums are emitted. Never element text, form values, request or
 *   response bodies, email addresses, UPNs or display names.
 * - **No duplication.** HTTP dependencies, page views, route changes and unhandled errors are
 *   collected automatically by the Application Insights SDK. Manual tracking is reserved for
 *   business-meaningful events the SDK cannot infer.
 * - **Dimension names match the BFF API** (`app.*`), so UI and API telemetry join in Kusto.
 */
export class TelemetryClient {
    private static instance: TelemetryClient;
    private appInsights?: ApplicationInsights;
    private readonly config: TelemetryConfiguration;
    private readonly sessionId: string;
    private readonly enabled: boolean;
    private userId?: string;
    private tenantId?: string;
    private static getUserAccountCallback: (() => { userId: string; tenantId?: string } | null) | null = null;

    private constructor(config: TelemetryConfiguration) {
        this.config = config;
        this.sessionId = this.generateSessionId();
        this.enabled = config.enabled !== false && !!config.configuration?.connectionString;

        if (this.enabled) {
            this.initializeAppInsights();
        }
    }

    /**
     * Set a callback used to resolve the current signed-in account.
     * The callback must return pseudonymous identifiers only (never a UPN or email).
     */
    public static setUserAccountCallback(getUserAccount: () => { userId: string; tenantId?: string } | null): void {
        TelemetryClient.getUserAccountCallback = getUserAccount;
    }

    /**
     * Get the telemetry instance. Returns undefined when telemetry has not been initialized yet
     * (call `initializeTelemetry` at app startup, or pass a config here).
     */
    public static getInstance(config?: TelemetryConfiguration): TelemetryClient | undefined {
        if (!TelemetryClient.instance && config) {
            TelemetryClient.instance = new TelemetryClient(config);
        }

        return TelemetryClient.instance;
    }

    /**
     * True when telemetry is configured and not disabled by the feature flag.
     */
    public get isEnabled(): boolean {
        return this.enabled;
    }

    private initializeAppInsights(): void {
        try {
            this.appInsights = new ApplicationInsights({
                config: {
                    ...this.config.configuration,
                    // Route changes and page-visit timing.
                    enableAutoRouteTracking: true,
                    autoTrackPageVisitTime: true,
                    // Propagate W3C trace context to the BFF so UI and API operations share
                    // an operation_Id. correlationHeaderDomains is required because the UI and
                    // API are served from different origins.
                    enableCorsCorrelation: true,
                    distributedTracingMode: 2 /* W3C */,
                    correlationHeaderDomains: this.config.correlationHeaderDomains ?? [],
                    // Error signal. Header/body capture stays off to avoid recording tokens or PII.
                    enableAjaxErrorStatusText: false,
                    enableUnhandledPromiseRejectionTracking: true,
                    enableRequestHeaderTracking: false,
                    enableResponseHeaderTracking: false
                }
            });

            this.appInsights.loadAppInsights();
            this.setupTelemetryInitializers();
        } catch (error) {
            // Telemetry must never break the app.
            console.error('Failed to initialize telemetry', error);
            this.appInsights = undefined;
        }
    }

    /**
     * Attach the identity and application dimensions carried by every item.
     */
    private setupTelemetryInitializers(): void {
        this.appInsights?.addTelemetryInitializer((envelope) => {
            envelope.tags = envelope.tags || {};
            envelope.data = envelope.data || {};

            envelope.tags['ai.cloud.role'] = 'DLP-Purview-UI';
            envelope.tags['ai.cloud.roleInstance'] = window.location.hostname;
            envelope.tags['ai.application.ver'] = this.config.version;
            envelope.tags['ai.session.id'] = this.sessionId;

            const account = TelemetryClient.getUserAccountCallback?.() ?? null;
            if (account) {
                this.userId = account.userId;
                this.tenantId = account.tenantId;
            }

            if (this.userId) {
                // Pseudonymous Entra object id, never a UPN.
                envelope.tags['ai.user.authUserId'] = this.userId;
            }

            const baseData = envelope.baseData || {};
            envelope.baseData = baseData;
            sanitizeDependencyEnvelope(envelope);
            envelope.data = {
                ...envelope.data,
                [TelemetryDimensions.SessionId]: this.sessionId,
                ...(this.userId ? { [TelemetryDimensions.UserId]: this.userId } : {}),
                ...(this.tenantId ? { [TelemetryDimensions.AuthTenantId]: this.tenantId } : {}),
                [TelemetryDimensions.Environment]: this.config.environmentName
            };

            return true;
        });
    }

    /**
     * Track a custom event. Properties must contain no personal data.
     */
    public trackEvent(name: string, properties?: Record<string, unknown>, measurements?: Record<string, number>): void {
        if (!this.appInsights) {
            return;
        }

        try {
            this.appInsights.trackEvent({ name, properties, measurements });
        } catch (error) {
            console.error('Failed to track event', error);
        }
    }

    /**
     * Track usage of an application feature (the primary UI business signal).
     */
    public trackFeatureUsage(event: FeatureUsageEvent): void {
        const { featureName, action, componentName, result, durationMs, ...rest } = event;

        this.trackEvent(
            TelemetryEventName.FeatureUsage,
            {
                ...rest,
                [TelemetryDimensions.Feature]: featureName,
                [TelemetryDimensions.Action]: action,
                [TelemetryDimensions.Component]: componentName,
                ...(result ? { [TelemetryDimensions.Result]: result } : {})
            },
            durationMs !== undefined ? { durationMs } : undefined
        );
    }

    /**
     * Track a condition the UI proactively surfaces to the user (degraded state, blocked action).
     */
    public trackProActiveAlert(event: ProActiveAlertEvent): void {
        const { alertType, severity, source, description, ...rest } = event;

        this.trackEvent(TelemetryEventName.ProActiveAlert, {
            ...rest,
            [TelemetryDimensions.AlertType]: alertType,
            [TelemetryDimensions.Severity]: severity,
            [TelemetryDimensions.Source]: source,
            [TelemetryDimensions.Description]: description
        });
    }

    /**
     * Track an error. Only the error type/message and caller-supplied, non-personal context
     * are recorded — never request or response payloads.
     */
    public trackError(error: Error, properties?: Record<string, unknown>): void {
        if (!this.appInsights) {
            return;
        }

        try {
            this.appInsights.trackException({
                exception: error,
                severityLevel: SeverityLevel.Error,
                properties
            });
        } catch (trackingError) {
            console.error('Failed to track error', trackingError);
        }
    }

    /**
     * Track a page view. Route changes are auto-tracked; call this only for views the router
     * does not represent as a navigation.
     */
    public trackPageView(name?: string, properties?: Record<string, unknown>): void {
        if (!this.appInsights) {
            return;
        }

        try {
            this.appInsights.trackPageView({ name, properties });
        } catch (error) {
            console.error('Failed to track page view', error);
        }
    }

    /**
     * Flush pending telemetry (for example before a sign-out redirect).
     */
    public flush(): void {
        this.appInsights?.flush();
    }

    /**
     * Generate a session identifier using cryptographically secure random values.
     */
    private generateSessionId(): string {
        const array = new Uint32Array(2);
        crypto.getRandomValues(array);
        const randomStr = array[0].toString(36) + array[1].toString(36);
        return `session_${Date.now()}_${randomStr.substring(0, 9)}`;
    }
}

/**
 * Initialize telemetry. Safe to call when telemetry is disabled — the returned client
 * becomes a no-op.
 */
export const initializeTelemetry = (config: TelemetryConfiguration): TelemetryClient => {
    return TelemetryClient.getInstance(config)!;
};
